import { onBeforeUnmount } from 'vue';

import ws from '@/plugins/cryptoPriceWS';
import type { PriceSocket } from '@/types/priceSocket';
import { useBotDetect } from '@paybis/frontend-common-lib/src/composables/useBotDetect';

const WS_CONNECT_DELAY_MS = 15_000;

export interface DeferredPriceSocket {
  initPriceSocket: () => void;
  suspendPriceSocket: () => void;
  resumePriceSocket: (market: string, fiat: string) => void;
}

// Defers the seo-prices websocket connect by 15s (unless the visitor is a bot) and
// lets a caller suspend/resume it around a price change without ever opening two
// sockets. The fiat is carried only by the connection URL, so market/fiat are passed
// as getters and resolved when connect() actually fires — never frozen at registration.
export const useDeferredPriceSocket = (
  namespace: string,
  market: () => string,
  fiat: () => string,
): DeferredPriceSocket => {
  let socket: PriceSocket | null = null;
  let wsConnect: (() => void) | null = null;
  let wsConnectStarted = false;
  let wsConnectTimeout = -1;
  let wsConnectDeadline = 0;

  const initPriceSocket = (): void => {
    // Client-only: never register the socket or arm a timer during SSR.
    if (typeof window === 'undefined') {
      return;
    }

    socket = ws(namespace, market, fiat);

    const { isBot } = useBotDetect();
    if (isBot) {
      return;
    }

    wsConnect = (): void => {
      if (wsConnectStarted) {
        return;
      }
      wsConnectStarted = true;
      socket?.connect();
    };
    wsConnectDeadline = Date.now() + WS_CONNECT_DELAY_MS;
    wsConnectTimeout = window.setTimeout(wsConnect, WS_CONNECT_DELAY_MS);
  };

  const suspendPriceSocket = (): void => {
    if (wsConnectStarted) {
      socket?.disconnect();

      return;
    }

    clearTimeout(wsConnectTimeout);
  };

  // The started branch reconnects to the explicit market/fiat the caller passes (the
  // user's current selection). The deferred branch re-arms against the original
  // deadline so repeated switches cannot push the connect out indefinitely, and
  // replaces the pending timer rather than stacking a second one (a second socket).
  const resumePriceSocket = (currentMarket: string, currentFiat: string): void => {
    if (wsConnectStarted) {
      socket?.connectToPrice(currentMarket, currentFiat);

      return;
    }

    if (wsConnect === null) {
      return;
    }

    clearTimeout(wsConnectTimeout);
    wsConnectTimeout = window.setTimeout(wsConnect, Math.max(0, wsConnectDeadline - Date.now()));
  };

  onBeforeUnmount(() => {
    clearTimeout(wsConnectTimeout);
    if (wsConnectStarted) {
      socket?.disconnect();
    }
  });

  return { initPriceSocket, suspendPriceSocket, resumePriceSocket };
};
